// Ship 2 (Path γ) — TypeScript port of briefing_room_backend/engine/tables.py.
//
// Eager-loads all 13 reference JSONs from @seed/ at module import time. Each
// lookup helper returns canonical Core Rulebook values; formulas live in
// engine/resolution.ts (Ship 1) and engine/modifiers.ts (Ship 3).
//
// Both engines (Python + TS) read the SAME canonical JSON files. The
// cross-language parity fixture at frontend/tests/engine-parity/cases.json
// verifies that the formula-based fast paths in resolution.ts produce
// outputs identical to Python; the JSON tables here let TS callers do the
// canonical lookup directly when they prefer the table-cell answer.
//
// Source-of-truth manifest:
//   docs/GAMES/RPG/007 JAMES BOND RPG/DOCS/20260601_CORE_RULEBOOK_AUTHORITATIVE_MANIFEST.md

import multiplicationTableData from '@seed/tables/multiplication_table.json';
import qualityResultsTableData from '@seed/tables/quality_results_table.json';
import damageClassData from '@seed/tables/damage_class.json';
import woundAccumulationData from '@seed/tables/wound_accumulation.json';
import areaWeaponDamageData from '@seed/tables/area_weapon_damage.json';
import fallDamageData from '@seed/tables/fall_damage.json';
import woundAdjustmentData from '@seed/tables/wound_adjustment.json';
import fireCombatModifiersData from '@seed/tables/fire_combat_modifiers.json';
import h2hModifiersData from '@seed/tables/h2h_modifiers.json';
import scarChanceData from '@seed/tables/scar_chance.json';
import scarLocationData from '@seed/tables/scar_location.json';
import skillsData from '@seed/skills.json';
import abilitiesData from '@seed/abilities.json';

// ─── Canonical enumerations (mirror tables.py:50-59) ─────────────────────

/** All recognized wound levels in canonical order (ST/LW/MW/HW/IN/KL). */
export const WOUND_LEVELS = ['ST', 'LW', 'MW', 'HW', 'IN', 'KL'] as const;
export type WoundLevel = (typeof WOUND_LEVELS)[number];

/** Wound levels that persist (used for accumulation). Excludes ST + KL. */
export const PERSISTENT_WOUND_LEVELS = ['LW', 'MW', 'HW', 'IN'] as const;
export type PersistentWoundLevel = (typeof PERSISTENT_WOUND_LEVELS)[number];

/** All 12 damage classes (A through L). */
export const DAMAGE_CLASSES = [
  'A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L',
] as const;
export type DamageClass = (typeof DAMAGE_CLASSES)[number];

/** 5 characteristic codes per Core Rulebook. */
export const CHARACTERISTICS = ['STR', 'DEX', 'WIL', 'PER', 'INT'] as const;
export type Characteristic = (typeof CHARACTERISTICS)[number];

/** 11 canonical Ease Factor positions including the ½ floor. */
export const EASE_FACTORS = [
  '0.5', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10',
] as const;
export type EaseFactor = (typeof EASE_FACTORS)[number];

/** 4 character ranks per Core p.18-21. */
export const CHARACTER_RANKS = ['Rookie', 'Agent', '00', 'Mastermind'] as const;
export type CharacterRank = (typeof CHARACTER_RANKS)[number];

/** 3 abilities all PC/NPC of intelligence agencies have at PC=20. */
export const ABILITY_NAMES = ['Connoisseur', 'First Aid', 'Photography'] as const;
export type AbilityName = (typeof ABILITY_NAMES)[number];

/** QR number → human-readable name (mirror tables.py:59 QR_NAMES). */
export const QR_NAMES: Record<1 | 2 | 3 | 4, string> = {
  1: 'Excellent',
  2: 'Very Good',
  3: 'Good',
  4: 'Acceptable',
};

// ─── JSON shape types (one per seed file consumed by lookups) ─────────────

interface MultiplicationTableJson {
  cells_by_pc_then_ef: Record<string, Record<string, number>>;
}

interface QualityResultsRow {
  sc_range: string;
  sc_min: number;
  sc_max: number;
  qr1: { roll_min: number; roll_max: number };
  qr2: { roll_min: number; roll_max: number };
  qr3: { roll_min: number; roll_max: number };
  qr4: { roll_min: number; roll_max?: number; roll_max_formula?: string };
}

interface QualityResultsTableJson {
  rows: QualityResultsRow[];
}

interface DamageClassCell {
  qr: 1 | 2 | 3 | 4;
  dc: DamageClass;
  wound_level: WoundLevel;
}

interface DamageClassJson {
  cells: DamageClassCell[];
}

interface WoundAccumulationCell {
  new: PersistentWoundLevel;
  old: PersistentWoundLevel;
  result: WoundLevel;
}

interface WoundAccumulationJson {
  cells: WoundAccumulationCell[];
}

interface AreaWeaponDamageCell {
  dc: 'I' | 'J' | 'K' | 'L';
  distance_band: '0-10' | '11-20' | '21-30' | '31-40';
  wound_level: WoundLevel;
}

interface AreaWeaponDamageJson {
  cells: AreaWeaponDamageCell[];
}

interface FallDamageRow {
  height_min: number;
  height_max: number | null;
  wound_level_code: WoundLevel | null;
}

interface FallDamageJson {
  rows: FallDamageRow[];
}

interface WoundAdjustmentRow {
  material: string;
  structure_points_min: number | null;
  structure_points_max: number | null;
  adjustment_levels: number;
}

interface WoundAdjustmentJson {
  rows: WoundAdjustmentRow[];
}

interface FireCombatModifierRow {
  ef_modifier: number;
  reason: string;
}

interface FireCombatModifiersJson {
  firer_modifiers: FireCombatModifierRow[];
  target_modifiers: FireCombatModifierRow[];
}

interface H2HModifierRow {
  ef_modifier: number;
  reason: string;
}

interface H2HModifiersJson {
  attacker_modifiers: H2HModifierRow[];
}

interface ScarChanceRow {
  wound_level: WoundLevel;
  scar_chance_pct: number;
}

interface ScarChanceJson {
  rows: ScarChanceRow[];
}

interface ScarLocationRow {
  d100_min: number;
  d100_max: number;
  location: string;
}

interface ScarLocationJson {
  rows: ScarLocationRow[];
}

type SkillFormulaPattern =
  | 'add_then_skill_level'
  | 'avg_then_add_skill_level'
  | 'wil_plus_charisma_skill_level_avg_then_add_skill_level';

interface SkillDefinition {
  name: string;
  formula_pattern: SkillFormulaPattern;
  characteristics: Characteristic[];
  [extra: string]: unknown;
}

interface SkillsJson {
  skills: SkillDefinition[];
}

interface AbilityDefinition {
  name: AbilityName;
  primary_chance: number;
  [extra: string]: unknown;
}

interface AbilitiesJson {
  abilities: AbilityDefinition[];
}

// ─── Eager-loaded constants (mirror tables.py:35-47) ─────────────────────

export const MULTIPLICATION_TABLE = multiplicationTableData as unknown as MultiplicationTableJson;
export const QUALITY_RESULTS_TABLE = qualityResultsTableData as unknown as QualityResultsTableJson;
export const WOUND_LEVEL_CHART = damageClassData as unknown as DamageClassJson;
export const WOUND_ACCUMULATION = woundAccumulationData as unknown as WoundAccumulationJson;
export const AREA_WEAPON_DAMAGE = areaWeaponDamageData as unknown as AreaWeaponDamageJson;
export const FALL_WOUND = fallDamageData as unknown as FallDamageJson;
export const WOUND_ADJUSTMENT = woundAdjustmentData as unknown as WoundAdjustmentJson;
export const FIRE_COMBAT_MODIFIERS = fireCombatModifiersData as unknown as FireCombatModifiersJson;
export const H2H_MODIFIERS = h2hModifiersData as unknown as H2HModifiersJson;
export const SCAR_CHANCE = scarChanceData as unknown as ScarChanceJson;
export const SCAR_LOCATION = scarLocationData as unknown as ScarLocationJson;
export const SKILLS = skillsData as unknown as SkillsJson;
export const ABILITIES = abilitiesData as unknown as AbilitiesJson;

// ─── Lookups (mirror tables.py:62-289 function-by-function) ──────────────

/** SC = PC × EF from the canonical Multiplication Table (Core p.158). */
export function multiplicationLookup(pc: number, ef: EaseFactor): number {
  if (!Number.isInteger(pc) || pc < 1 || pc > 30) {
    throw new Error(`Primary Chance must be 1-30, got ${pc}`);
  }
  if (!(EASE_FACTORS as readonly string[]).includes(ef)) {
    throw new Error(
      `Ease Factor must be one of ${EASE_FACTORS.join(', ')}, got ${JSON.stringify(ef)}`,
    );
  }
  return MULTIPLICATION_TABLE.cells_by_pc_then_ef[String(pc)]![ef]!;
}

/**
 * QR (1-4) for SC + d100. null if failure (roll == 100 OR roll > SC).
 * Mirrors tables.py:76 quality_results_lookup. Sources: Core p.157 + p.10.
 */
export function qualityResultsLookup(sc: number, roll: number): 1 | 2 | 3 | 4 | null {
  if (!Number.isInteger(sc) || sc < 1) {
    throw new Error(`Success Chance must be >= 1, got ${sc}`);
  }
  if (!Number.isInteger(roll) || roll < 1 || roll > 100) {
    throw new Error(`d100 roll must be 1-100, got ${roll}`);
  }
  if (roll === 100) return null;
  if (roll > sc) return null;

  // Find the SC range row.
  let row: QualityResultsRow | undefined;
  for (const r of QUALITY_RESULTS_TABLE.rows) {
    if (r.sc_min <= sc && sc <= r.sc_max) {
      row = r;
      break;
    }
  }
  if (!row) {
    // SC > 300: Core doesn't define this; clamp to top row.
    row = QUALITY_RESULTS_TABLE.rows[QUALITY_RESULTS_TABLE.rows.length - 1]!;
  }

  for (const qrNum of [1, 2, 3] as const) {
    const cell = row[`qr${qrNum}` as 'qr1' | 'qr2' | 'qr3'];
    if (cell.roll_min <= roll && roll <= cell.roll_max) {
      return qrNum;
    }
  }

  const qr4 = row.qr4;
  let actualMax: number;
  if (qr4.roll_max_formula !== undefined) {
    actualMax = Math.min(sc, 99);
  } else {
    actualMax = qr4.roll_max!;
  }
  if (qr4.roll_min <= roll && roll <= actualMax) {
    return 4;
  }

  return null;
}

/** Wound Level Chart lookup (Core p.46). DC × QR → ST/LW/MW/HW/IN/KL. */
export function woundLevelLookup(dc: DamageClass, qr: 1 | 2 | 3 | 4): WoundLevel {
  if (!(DAMAGE_CLASSES as readonly string[]).includes(dc)) {
    throw new Error(`Damage Class must be one of ${DAMAGE_CLASSES.join(', ')}, got ${dc}`);
  }
  if (![1, 2, 3, 4].includes(qr)) {
    throw new Error(`QR must be 1-4, got ${qr}`);
  }
  for (const cell of WOUND_LEVEL_CHART.cells) {
    if (cell.qr === qr && cell.dc === dc) {
      return cell.wound_level;
    }
  }
  throw new Error(`No Wound Level Chart cell for QR=${qr}, DC=${dc}`);
}

/** Wound Accumulation Chart lookup (Core p.48). Stacks NEW × OLD → result. */
export function woundAccumulationLookup(
  newWound: PersistentWoundLevel,
  oldWound: PersistentWoundLevel,
): WoundLevel {
  if (!(PERSISTENT_WOUND_LEVELS as readonly string[]).includes(newWound)) {
    throw new Error(
      `New wound must be one of ${PERSISTENT_WOUND_LEVELS.join(', ')}, got ${newWound}`,
    );
  }
  if (!(PERSISTENT_WOUND_LEVELS as readonly string[]).includes(oldWound)) {
    throw new Error(
      `Old wound must be one of ${PERSISTENT_WOUND_LEVELS.join(', ')}, got ${oldWound}`,
    );
  }
  for (const cell of WOUND_ACCUMULATION.cells) {
    if (cell.new === newWound && cell.old === oldWound) {
      return cell.result;
    }
  }
  throw new Error(`No Wound Accumulation cell for new=${newWound}, old=${oldWound}`);
}

/** Area Weapon Damage Chart lookup (Core p.48). DC × distance → wound. */
export function areaWeaponDamageLookup(
  dc: 'I' | 'J' | 'K' | 'L',
  distanceFeet: number,
): WoundLevel {
  if (!['I', 'J', 'K', 'L'].includes(dc)) {
    throw new Error(`Area Weapon DC must be I/J/K/L, got ${dc}`);
  }
  if (!Number.isInteger(distanceFeet) || distanceFeet < 0) {
    throw new Error(`Distance must be >= 0 feet, got ${distanceFeet}`);
  }
  if (distanceFeet > 40) {
    throw new Error(`Distance must be <= 40 feet (beyond chart), got ${distanceFeet}`);
  }
  let band: '0-10' | '11-20' | '21-30' | '31-40';
  if (distanceFeet <= 10) band = '0-10';
  else if (distanceFeet <= 20) band = '11-20';
  else if (distanceFeet <= 30) band = '21-30';
  else band = '31-40';

  for (const cell of AREA_WEAPON_DAMAGE.cells) {
    if (cell.dc === dc && cell.distance_band === band) {
      return cell.wound_level;
    }
  }
  throw new Error(`No Area Weapon Damage cell for DC=${dc}, band=${band}`);
}

/**
 * Fall Wound Chart lookup (Core p.47). Height in feet → wound or null
 * (0-10 ft = no damage).
 */
export function fallDamageLookup(heightFeet: number): WoundLevel | null {
  if (!Number.isInteger(heightFeet) || heightFeet < 0) {
    throw new Error(`Height must be >= 0, got ${heightFeet}`);
  }
  for (const row of FALL_WOUND.rows) {
    const hmin = row.height_min;
    const hmax = row.height_max;
    if (hmin <= heightFeet && (hmax === null || heightFeet <= hmax)) {
      return row.wound_level_code;
    }
  }
  throw new Error(`No Fall Wound Chart row for height ${heightFeet}`);
}

/** Scar chance % for a wound level (Core p.53). MW=5, HW=15, IN=35; else 0. */
export function scarChancePctFor(woundLevel: WoundLevel): number {
  if (!(WOUND_LEVELS as readonly string[]).includes(woundLevel)) {
    throw new Error(
      `Wound level must be one of ${WOUND_LEVELS.join(', ')}, got ${woundLevel}`,
    );
  }
  for (const row of SCAR_CHANCE.rows) {
    if (row.wound_level === woundLevel) {
      return row.scar_chance_pct;
    }
  }
  return 0;
}

/** Scar location from d100 roll (Core p.53). */
export function scarLocationLookup(d100Roll: number): string {
  if (!Number.isInteger(d100Roll) || d100Roll < 1 || d100Roll > 100) {
    throw new Error(`d100 must be 1-100, got ${d100Roll}`);
  }
  for (const row of SCAR_LOCATION.rows) {
    if (row.d100_min <= d100Roll && d100Roll <= row.d100_max) {
      return row.location;
    }
  }
  throw new Error(`No Scar Location row for d100=${d100Roll}`);
}

/** Full skill definition from seed/skills.json. Throws if unknown. */
export function getSkillDefinition(skillName: string): SkillDefinition {
  const skill = SKILLS.skills.find((s) => s.name === skillName);
  if (!skill) {
    const valid = SKILLS.skills.map((s) => s.name).join(', ');
    throw new Error(`No skill named ${JSON.stringify(skillName)}; valid: ${valid}`);
  }
  return skill;
}

/** Full ability definition from seed/abilities.json. */
export function getAbilityDefinition(abilityName: AbilityName): AbilityDefinition {
  if (!(ABILITY_NAMES as readonly string[]).includes(abilityName)) {
    throw new Error(
      `Ability must be one of ${ABILITY_NAMES.join(', ')}, got ${JSON.stringify(abilityName)}`,
    );
  }
  const ability = ABILITIES.abilities.find((a) => a.name === abilityName);
  if (!ability) {
    throw new Error(`No ability named ${JSON.stringify(abilityName)}`);
  }
  return ability;
}

/**
 * Skill Use Chart for a QR (Core p.32). qr=null represents failure
 * (3× base time, false information).
 */
export interface SkillUseChartResult {
  time_multiplier: number;
  information_pct: number;
  is_failure: boolean;
}

export function skillUseChartLookup(qr: 1 | 2 | 3 | 4 | null): SkillUseChartResult {
  if (qr === null) return { time_multiplier: 3.0, information_pct: 0, is_failure: true };
  if (qr === 4) return { time_multiplier: 2.0, information_pct: 50, is_failure: false };
  if (qr === 3) return { time_multiplier: 1.0, information_pct: 75, is_failure: false };
  if (qr === 2) return { time_multiplier: 0.5, information_pct: 90, is_failure: false };
  if (qr === 1) return { time_multiplier: 0.25, information_pct: 100, is_failure: false };
  throw new Error(`QR must be 1-4 or null, got ${qr}`);
}

/** Fire Combat EF modifier by reason × side ('firer' | 'target'). */
export function fireCombatModifierFor(reason: string, side: 'firer' | 'target'): number {
  const mods =
    side === 'firer'
      ? FIRE_COMBAT_MODIFIERS.firer_modifiers
      : side === 'target'
        ? FIRE_COMBAT_MODIFIERS.target_modifiers
        : null;
  if (mods === null) {
    throw new Error(`Side must be 'firer' or 'target', got ${JSON.stringify(side)}`);
  }
  for (const m of mods) {
    if (m.reason === reason) return m.ef_modifier;
  }
  throw new Error(`No Fire Combat modifier for reason=${JSON.stringify(reason)} on ${side} side`);
}

/** H2H attacker EF modifier (Core p.51). */
export function h2hAttackerModifierFor(reason: string): number {
  for (const m of H2H_MODIFIERS.attacker_modifiers) {
    if (m.reason === reason) return m.ef_modifier;
  }
  throw new Error(`No H2H attacker modifier for reason=${JSON.stringify(reason)}`);
}

/**
 * Wound level reduction for damage to material objects (Core p.48).
 * For Vehicles, structurePoints is required.
 */
export function woundAdjustmentLevelsFor(
  material: string,
  structurePoints?: number | null,
): number {
  for (const row of WOUND_ADJUSTMENT.rows) {
    if (row.material === material) {
      if (material === 'Vehicles') {
        if (structurePoints === undefined || structurePoints === null) {
          throw new Error("structure_points required for material='Vehicles'");
        }
        const spmin = row.structure_points_min;
        const spmax = row.structure_points_max;
        if (
          (spmin === null || structurePoints >= spmin) &&
          (spmax === null || structurePoints <= spmax)
        ) {
          return row.adjustment_levels;
        }
      } else {
        return row.adjustment_levels;
      }
    }
  }
  throw new Error(
    `No Wound Adjustment row for material=${JSON.stringify(material)}, sp=${structurePoints}`,
  );
}
